import Utils from "../../../utils/utils";
import ROLE from "../../../modules/role";
import { ROUTES } from "../../../routes";
import { Dropdown } from "react-bootstrap";
import ApiGroup from "../../../modules/api/group";
import useDebounce from "../../../hooks/use-debounce";
import usePageTitle from "../../../hooks/use-page-title";
import Notifier from "../../../modules/notifier/notifier";
import AppUserContext from "../../../context/app-user-context";
import { Link, useParams, useLocation } from "react-router-dom";
import { PortalContext, usePortalSettingSet } from "../../../context/portal-context";
import { MdDelete, MdModeEditOutline, MdSearch } from "react-icons/md";
import BasicDataTable from "../../../components/common/BasicDataTable";
import React, { useContext, useEffect, useRef, useState } from "react";
import FooterBoxAction from "../../../components/common/FooterBoxAction";
import PagePortalNotFound from "../PagePortalNotFound/PagePortalNotFound";
import PopupImportMembers, {
  TPopupImportMembersRef,
} from "../../../components/popup/PopupImportMembers";
import ModulePopup from "../../../modules/popup/popup";
import Debouncer from "../../../modules/debouncer/debouncer";
import RoleFilterSelector from "../../../components/portal/RoleFilterSelector";

export default function PageGroupManageMember() {
  const title = "Manage Members";
  usePageTitle(title);
  usePortalSettingSet("label_header", title);
  const { setCustomText } = useContext(PortalContext);
  const location = useLocation();
  const user = useContext(AppUserContext);
  const { programmeId, cohortId, _id } = useParams();
  const isValidProgrammeId = user.programmeIds.includes(programmeId);

  const refPopupImportSupervisors = useRef<TPopupImportMembersRef>();
  const refPopupImportIndustrySupervisors = useRef<TPopupImportMembersRef>();
  const refPopupImportStudents = useRef<TPopupImportMembersRef>();

  const [refresh, setRefresh] = useState(false);
  const [group, setGroup] = useState<any>({});
  const [groupLoading, setGroupLoading] = useState(true);
  const [groupFound, setGroupFound] = useState(false);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersSit, setMembersSit] = useState([]);
  const [membersIndustry, setMembersIndustry] = useState([]);
  const [membersStudent, setMembersStudent] = useState([]);
  const [sitSearchText, setSitSearchText] = useState(null);
  const [industrySearchText, setIndustrySearchText] = useState(null);
  const [studentSearchText, setStudentSearchText] = useState(null);

  const sitSearch = useDebounce(sitSearchText, 500);
  const industrySearch = useDebounce(industrySearchText, 500);
  const studentSearch = useDebounce(studentSearchText, 500);

  const [selectedPillOption, setSelectedPillOption] = useState("sit_supervisor");
  const [pillOptions, setPillOptions] = useState([
    { label: "SIT Supervisors", value: "sit_supervisor" },
    { label: "Industry Supervisors", value: "industry_supervisors" },
    { label: "Students", value: "students" },
  ]);

  const roles = {
    sit_supervisor: ROLE.SUPERVISOR,
    industry_supervisors: ROLE.INDUSTRY_SUPERVISOR,
    students: ROLE.STUDENT,
  };

  const locationState = { ...location.state };
  delete locationState.previous;

  useEffect(() => {
    setCustomText(title);
  }, []);

  useEffect(() => {
    if (!isValidProgrammeId) return;
    setGroupLoading(true);
    ApiGroup.get(_id).then((res) => {
      if (res.status) {
        setGroup(res.data);
        setGroupFound(true);
      }
      setGroupLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!isValidProgrammeId) return;
    if (membersLoading) return;
    setMembersLoading(true);
    ApiGroup.getUsersByGroupId(_id)
      .then((data) => setMembers(data))
      .catch((e) => Notifier.error(e.message))
      .finally(() => setMembersLoading(false));
  }, [refresh]);

  useEffect(() => {
    if (!Array.isArray(members)) return;
    let sit = members.filter(
      (m) =>
        m.roles.includes(ROLE.SUPERVISOR) &&
        m.groups.some((_) => _.group === _id && _.role === ROLE.SUPERVISOR)
    );
    if (sitSearch) {
      let regex = new RegExp(Utils.escapeRegExp(sitSearch), "i");
      sit = sit.filter((_) => regex.test(_.name) || regex.test(_.email));
    }
    setMembersSit(sit);
  }, [members, sitSearch]);

  useEffect(() => {
    if (!Array.isArray(members)) return;
    let industry = members.filter(
      (m) =>
        m.roles.includes(ROLE.INDUSTRY_SUPERVISOR) &&
        m.groups.some((_) => _.group === _id && _.role === ROLE.INDUSTRY_SUPERVISOR)
    );
    if (industrySearch) {
      let regex = new RegExp(Utils.escapeRegExp(industrySearch), "i");
      industry = industry.filter(
        (_) => regex.test(_.name) || regex.test(_.email)
      );
    }
    setMembersIndustry(industry);
  }, [members, industrySearch]);

  useEffect(() => {
    if (!Array.isArray(members)) return;
    let student = members.filter(
      (m) =>
        m.roles.includes(ROLE.STUDENT) &&
        m.groups.some((_) => _.group === _id && _.role === ROLE.STUDENT)
    );
    if (studentSearch) {
      let regex = new RegExp(Utils.escapeRegExp(studentSearch), "i");
      student = student.filter(
        (_) => regex.test(_.name) || regex.test(_.email)
      );
    }
    setMembersStudent(student);
  }, [members, studentSearch]);

  useEffect(() => {
    if (!Array.isArray(members)) return;
    setPillOptions([
      {
        label: `SIT Supervisors (${
          members.filter(
            (m) =>
              m.roles.includes(ROLE.SUPERVISOR) &&
              m.groups.some((_) => _.group === _id && _.role === ROLE.SUPERVISOR)
          ).length
        })`,
        value: "sit_supervisor",
      },
      {
        label: `Industry Supervisors (${
          members.filter(
            (m) =>
              m.roles.includes(ROLE.INDUSTRY_SUPERVISOR) &&
              m.groups.some((_) => _.group === _id && _.role === ROLE.INDUSTRY_SUPERVISOR)
          ).length
        })`,
        value: "industry_supervisors",
      },
      {
        label: `Students (${
          members.filter(
            (m) =>
              m.roles.includes(ROLE.STUDENT) &&
              m.groups.some((_) => _.group === _id && _.role === ROLE.STUDENT)
          ).length
        })`,
        value: "students",
      },
    ]);
  }, [members]);

  useEffect(() => {
    if (user.referenceUserId) {
      const savedOption = localStorage.getItem(`InternalSettings_${cohortId}_${user.referenceUserId}`);
      if (savedOption) {
        setSelectedPillOption(savedOption);
      } else {
        setSelectedPillOption("sit_supervisor");
        localStorage.setItem(`InternalSettings_${cohortId}_${user.referenceUserId}`, "sit_supervisor");
      }
    }
  }, [user, cohortId]);

  const MemberDropdown = (props: { member: any }) => (
    <Dropdown align={"end"} drop={"down"}>
      <Dropdown.Toggle variant="secondary" id="dropdown-basic" className="btn-sm">
        Manage
      </Dropdown.Toggle>
      <Dropdown.Menu renderOnMount popperConfig={{ strategy: "fixed" }}>
        <Link
          className="dropdown-item"
          to={`${ROUTES.PORTAL_MEMBERS}/edit/${programmeId}/${cohortId}/${_id}/${roles[selectedPillOption]}/${props.member._id}`}
        >
          <MdModeEditOutline className="cursor-pointer icon-action-default" /> Edit Member
        </Link>
        <Dropdown.Item onClick={() => removeMember(props.member)}>
          <MdDelete /> Remove Member
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );

  const removeMember = (member: any) => {
    ModulePopup.showAlertConfirm({
      title: "Confirmation",
      description: (
        <>
          <p>
            Are you sure you want to remove this member?
            <br/>
            <br /> <strong>This action cannot be undone!</strong>
          </p>
        </>
      ),
      onYes: () =>
        Debouncer.execute("DELETE_MEMBER", async () => {
          const res = await ApiGroup.removeMember(_id, member._id, roles[selectedPillOption]);
          if (res.status) {
            const membersTemp = [...members];
            const unassigned = membersTemp.find((_) => _._id === member._id);
            unassigned.groups = unassigned.groups.filter(
              (_) => _.group !== _id || _.role !== roles[selectedPillOption]
            );
            setMembers(membersTemp);
            Notifier.success(res.message);
          } else {
            Notifier.error(res.message);
          }
        }),
    });
  };

  if (
    !isValidProgrammeId ||
    (group &&
      group.cohort &&
      (!group.cohort.programme || group.cohort.programme._id !== programmeId))
  ) {
    return <PagePortalNotFound />;
  }
  return (
    <main className="form-generic content-generic content-generic--fluid">
      {/* <h1 className="fs-title hide-mobile-down">{title}</h1> */}

      {groupLoading && (
        <p className="alert alert-info mt-3">Loading detail...</p>
      )}

      {!groupLoading && !groupFound && (
        <p className="alert alert-danger mt-3">
          Group not found.{" "}
          <Link
            to={`${ROUTES.PORTAL_GROUPS}/${programmeId}/${cohortId}`}
            state={locationState}
          >
            Click here to go back
          </Link>
          .
        </p>
      )}

      {!groupLoading && groupFound && (
        <React.Fragment>
          <p>
            {group.cohort && (
              <>
                {/* Programme: <strong>{group.cohort.programme?.name ?? ""}</strong> */}
                <h1 className="fs-title hide-mobile-down mb-0">Programme: <strong>{group.cohort.programme?.name ?? ""}</strong></h1>
                <br />
                Cohort: <strong>{group.cohort.name}</strong>
                <br />
              </>
            )}
            <span className="d-none">Number of members: <strong>{members.length}</strong></span>
          </p>

          <div className="cg__actions">
            <div className="cg__action-buttons">
              <button
                className="btn btn-secondary"
                onClick={() => refPopupImportSupervisors.current.show()}
              >
                Import SIT Supervisors
              </button>
              {" "}
              <button
                className="btn btn-secondary"
                onClick={() => refPopupImportIndustrySupervisors.current.show()}
              >
                Import Industry Supervisors
              </button>
              {" "}
              <button
                className={`pointer-events-all btn btn-secondary ${(membersSit.length === 0 || membersIndustry.length === 0) ? "disabled" : ""}`}
                onClick={() => {
                  if (membersSit.length === 0 || membersIndustry.length === 0) {
                    ModulePopup.showAlert({
                      title: "Alert",
                      description: "Please first import the SIT Supervisors and then the Industry Supervisors, before importing Students."
                    })
                  } else {
                    refPopupImportStudents.current.show()
                  }
                }}
              >
                Import Students
              </button>

              {/* Clients don't want us to show this Refresh button */}
              {false && (
                <button
                  className="btn btn-outline-primary"
                  onClick={() => setRefresh(!refresh)}
                >
                  Refresh
                </button>
              )}
            </div>
          </div>

          <RoleFilterSelector default={selectedPillOption}
            options={pillOptions}
            onChange={(v) => {
              setSelectedPillOption(v);
              localStorage.setItem(`InternalSettings_${cohortId}_${user.referenceUserId}`, v);
            }}
          />

          {selectedPillOption === "sit_supervisor" && (
            <div className="my-2">
              <div className="d-flex flex-wrap align-items-center justify-content-end mb-2">
                <div className="cg__action-search">
                  <span>
                    <MdSearch />
                  </span>
                  <input
                    type="text"
                    placeholder="Search"
                    onChange={(e) => setSitSearchText(e.target.value)}
                  />
                </div>
              </div>
              <BasicDataTable
                pagination={false}
                loading={membersLoading}
                columns={[
                  { id: "name", name: "SIT Supervisor's Name", cell: (row) => row.name, sortable: true },
                  { id: "email", name: "SIT Login ID", cell: (row) => row.email, sortable: true },
                  { id: "nameEmail", name: "SIT Email Address", cell: (row) => row.nameEmail, sortable: true },
                  {
                    id: "students",
                    name: "Number of Students",
                    cell: (row) => {
                      return membersStudent.filter(student => {
                        if (!student.sitSupervisorName) return false;
                        const supervisors = student.sitSupervisorName.split(', ');
                        return supervisors.includes(row.name);
                      }).length;
                    }
                  },
                  { name: "", button: true, cell: (row) => <MemberDropdown member={row} /> },
                ]}
                data={membersSit}
              />
            </div>
          )}

          {selectedPillOption === "industry_supervisors" && (
            <div className="my-2">
              <div className="d-flex flex-wrap align-items-center justify-content-end mb-2">
                <div className="cg__action-search">
                  <span>
                    <MdSearch />
                  </span>
                  <input
                    type="text"
                    placeholder="Search"
                    onChange={(e) => setIndustrySearchText(e.target.value)}
                  />
                </div>
              </div>
              <BasicDataTable
                pagination={false}
                loading={membersLoading}
                columns={[
                  { id: "name", name: "Industry Supervisor's Name", cell: (row) => row.name, sortable: true },
                  { id: "email", name: "Email Address", cell: (row) => row.email, sortable: true },
                  { id: "company", name: "Employer", cell: (row) => row.organizationName, sortable: true },
                  {
                    id: "students",
                    name: "Number of Students",
                    cell: (row) => {
                      return membersStudent.filter(student => {
                        if (!student.industrySupervisorName) return false;
                        const supervisors = student.industrySupervisorName.split(', ');
                        return supervisors.includes(row.name);
                      }).length;
                    }
                  },
                  { name: "", button: true, cell: (row) => <MemberDropdown member={row} /> },
                ]}
                data={membersIndustry}
              />
            </div>
          )}

          {selectedPillOption === "students" && (
            <div className="my-2">
              <div className="d-flex flex-wrap align-items-center justify-content-end mb-2">
                <div className="cg__action-search">
                  <span>
                    <MdSearch />
                  </span>
                  <input
                    type="text"
                    placeholder="Search"
                    onChange={(e) => setStudentSearchText(e.target.value)}
                  />
                </div>
              </div>
              <BasicDataTable
                pagination={false}
                loading={membersLoading}
                columns={[
                  { id: "name", name: "Student's Name", cell: (row) => row.name, sortable: true },
                  { id: "email", name: "SIT Email Address", cell: (row) => row.email, sortable: true, minWidth: "300px" },
                  { id: "company", name: "Employer", cell: (row) => row.organizationName, sortable: true },
                  { id: "sitSupervisor", name: "SIT Supervisor", cell: (row) => row.sitSupervisorName },
                  { id: "industrySupervisor", name: "Industry Supervisor", cell: (row) => row.industrySupervisorName },
                  { id: "jobTitle", name: "Job Title", cell: (row) => row.jobTitle, sortable: true },
                  { name: "", button: true, cell: (row) => <MemberDropdown member={row} /> },
                ]}
                data={membersStudent}
              />
            </div>
          )}

          <PopupImportMembers
            entity="SIT Supervisor"
            title="Import SIT Supervisors"
            ref={refPopupImportSupervisors}
            role={ROLE.SUPERVISOR}
            groupId={_id}
            sampleFile="/assets/sample/GROW-Pro SIT Supervisors Enrolment Template.csv?t=20250821"
            sampleFileName="GROW-Pro SIT Supervisors Enrolment Template.csv"
            onImport={() => setRefresh(!refresh)}
          />
          <PopupImportMembers
            entity="Industry Supervisor"
            title="Import Industry Supervisors"
            ref={refPopupImportIndustrySupervisors}
            role={ROLE.INDUSTRY_SUPERVISOR}
            groupId={_id}
            sampleFile="/assets/sample/GROW-Pro Industry Supervisors Enrolment Template.csv?t=20250821"
            sampleFileName="GROW-Pro Industry Supervisors Enrolment Template.csv"
            onImport={() => setRefresh(!refresh)}
          />
          <PopupImportMembers
            entity="Student"
            title="Import Students"
            ref={refPopupImportStudents}
            role={ROLE.STUDENT}
            groupId={_id}
            sampleFile="/assets/sample/GROW-Pro Students Enrolment Template.csv?t=20250821"
            sampleFileName="GROW-Pro Students Enrolment Template.csv"
            onImport={() => setRefresh(!refresh)}
          />
        </React.Fragment>
      )}

      <FooterBoxAction
        backUrl={
          location.state?.previous ??
          `${ROUTES.PORTAL_COHORTS}/${programmeId}`
        }
        backUrlState={locationState}
      />
    </main>
  );
}
